import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Select, Text, Skeleton, Tabs, Badge, SimpleGrid } from '@mantine/core';
import { IconSearch, IconUsers, IconMapPin } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { useGoogleAdsAudiences, useGoogleAdsLocations, useGoogleAdsAccount } from '@/hooks/useGoogleAds';
import { microsToAmount } from '@/types/googleAds';
import type { DateRangeOption, Audience, LocationTarget } from '@/types/googleAds';
import { MetricCard } from '../components/MetricCard';

interface AudiencesTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

export function AudiencesTab({ customerId }: AudiencesTabProps) {
  const { data: audiences, isLoading: audiencesLoading } = useGoogleAdsAudiences(customerId);
  const { data: locations, isLoading: locationsLoading } = useGoogleAdsLocations(customerId);
  const { data: account } = useGoogleAdsAccount(customerId);

  const [activeTab, setActiveTab] = useState<string | null>('audiences');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [audienceSort, setAudienceSort] = useState<DataTableSortStatus<Audience>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const [locationSort, setLocationSort] = useState<DataTableSortStatus<LocationTarget>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  const isLoading = audiencesLoading || locationsLoading;

  const filteredAudiences = audiences
    ?.filter((a) => {
      if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter && a.type !== typeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = audienceSort.columnAccessor;
      let aVal: number;
      let bVal: number;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof Audience['metrics'];
        aVal = a.metrics[key];
        bVal = b.metrics[key];
      } else {
        return 0;
      }

      return audienceSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const filteredLocations = locations
    ?.filter((l) => {
      if (search && !l.locationName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = locationSort.columnAccessor;
      let aVal: number;
      let bVal: number;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof LocationTarget['metrics'];
        aVal = a.metrics[key];
        bVal = b.metrics[key];
      } else {
        return 0;
      }

      return locationSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const uniqueAudienceTypes = [...new Set(audiences?.map((a) => a.type) ?? [])];

  const audienceStats = {
    totalAudiences: audiences?.length ?? 0,
    targetedAudiences: audiences?.filter((a) => a.targetType === 'TARGET').length ?? 0,
    observedAudiences: audiences?.filter((a) => a.targetType === 'OBSERVE').length ?? 0,
    excludedAudiences: audiences?.filter((a) => a.targetType === 'EXCLUDE').length ?? 0,
  };

  const locationStats = {
    totalLocations: locations?.length ?? 0,
    targetedLocations: locations?.filter((l) => l.targetType === 'TARGET').length ?? 0,
    excludedLocations: locations?.filter((l) => l.targetType === 'EXCLUDE').length ?? 0,
    countries: locations?.filter((l) => l.locationType === 'COUNTRY').length ?? 0,
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
          {activeTab === 'audiences' && (
            <Select
              placeholder="Zielgruppentyp"
              data={[
                { value: '', label: 'Alle Typen' },
                ...uniqueAudienceTypes.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
              ]}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v ?? '')}
              clearable
              w={200}
            />
          )}
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="audiences" leftSection={<IconUsers size={16} />}>
            Zielgruppen{' '}
            <Badge size="sm" variant="light" ml={4}>
              {audiences?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="locations" leftSection={<IconMapPin size={16} />}>
            Standorte{' '}
            <Badge size="sm" variant="light" ml={4}>
              {locations?.length ?? 0}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="audiences" pt="md">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              <MetricCard
                title="Gesamt"
                value={audienceStats.totalAudiences}
                color="blue"
              />
              <MetricCard
                title="Targetiert"
                value={audienceStats.targetedAudiences}
                color="green"
              />
              <MetricCard
                title="Beobachtet"
                value={audienceStats.observedAudiences}
                color="yellow"
              />
              <MetricCard
                title="Ausgeschlossen"
                value={audienceStats.excludedAudiences}
                color="red"
              />
            </SimpleGrid>

            <DataTable
              withTableBorder
              borderRadius="sm"
              striped
              highlightOnHover
              records={filteredAudiences ?? []}
              sortStatus={audienceSort}
              onSortStatusChange={setAudienceSort}
              columns={[
                {
                  accessor: 'name',
                  title: 'Zielgruppe',
                  width: 300,
                  render: (audience) => (
                    <div>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {audience.name}
                      </Text>
                      {audience.campaignName && (
                        <Text size="xs" c="dimmed">
                          {audience.campaignName}
                          {audience.adGroupName && ` / ${audience.adGroupName}`}
                        </Text>
                      )}
                    </div>
                  ),
                },
                {
                  accessor: 'type',
                  title: 'Typ',
                  width: 150,
                  render: (audience) => (
                    <Badge variant="light" size="sm">
                      {audience.type.replace(/_/g, ' ')}
                    </Badge>
                  ),
                },
                {
                  accessor: 'targetType',
                  title: 'Targeting',
                  width: 120,
                  render: (audience) => (
                    <Badge
                      color={
                        audience.targetType === 'TARGET'
                          ? 'green'
                          : audience.targetType === 'OBSERVE'
                            ? 'yellow'
                            : 'red'
                      }
                      variant="light"
                      size="sm"
                    >
                      {audience.targetType === 'TARGET'
                        ? 'Targeting'
                        : audience.targetType === 'OBSERVE'
                          ? 'Beobachtung'
                          : 'Ausschluss'}
                    </Badge>
                  ),
                },
                {
                  accessor: 'bidModifier',
                  title: 'Gebotsanpassung',
                  width: 130,
                  textAlign: 'right',
                  render: (audience) =>
                    audience.bidModifier
                      ? `${audience.bidModifier > 0 ? '+' : ''}${((audience.bidModifier - 1) * 100).toFixed(0)}%`
                      : '-',
                },
                {
                  accessor: 'metrics.impressions',
                  title: 'Impr.',
                  sortable: true,
                  textAlign: 'right',
                  width: 100,
                  render: (audience) => formatNumber(audience.metrics.impressions),
                },
                {
                  accessor: 'metrics.clicks',
                  title: 'Klicks',
                  sortable: true,
                  textAlign: 'right',
                  width: 80,
                  render: (audience) => formatNumber(audience.metrics.clicks),
                },
                {
                  accessor: 'metrics.costMicros',
                  title: 'Kosten',
                  sortable: true,
                  textAlign: 'right',
                  width: 100,
                  render: (audience) => formatCurrency(audience.metrics.costMicros),
                },
                {
                  accessor: 'metrics.conversions',
                  title: 'Conv.',
                  sortable: true,
                  textAlign: 'right',
                  width: 80,
                  render: (audience) => audience.metrics.conversions.toFixed(1),
                },
              ]}
              noRecordsText="Keine Zielgruppen gefunden"
              minHeight={400}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="locations" pt="md">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              <MetricCard
                title="Gesamt"
                value={locationStats.totalLocations}
                color="blue"
              />
              <MetricCard
                title="Targetiert"
                value={locationStats.targetedLocations}
                color="green"
              />
              <MetricCard
                title="Ausgeschlossen"
                value={locationStats.excludedLocations}
                color="red"
              />
              <MetricCard
                title="Länder"
                value={locationStats.countries}
                color="cyan"
              />
            </SimpleGrid>

            <DataTable
              withTableBorder
              borderRadius="sm"
              striped
              highlightOnHover
              records={filteredLocations ?? []}
              sortStatus={locationSort}
              onSortStatusChange={setLocationSort}
              columns={[
                {
                  accessor: 'locationName',
                  title: 'Standort',
                  width: 250,
                  render: (location) => (
                    <div>
                      <Text size="sm" fw={500}>
                        {location.locationName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {location.campaignName}
                      </Text>
                    </div>
                  ),
                },
                {
                  accessor: 'locationType',
                  title: 'Typ',
                  width: 120,
                  render: (location) => (
                    <Badge variant="light" size="sm">
                      {location.locationType === 'COUNTRY'
                        ? 'Land'
                        : location.locationType === 'REGION'
                          ? 'Region'
                          : location.locationType === 'CITY'
                            ? 'Stadt'
                            : location.locationType === 'POSTAL_CODE'
                              ? 'PLZ'
                              : 'Radius'}
                    </Badge>
                  ),
                },
                {
                  accessor: 'targetType',
                  title: 'Targeting',
                  width: 120,
                  render: (location) => (
                    <Badge
                      color={location.targetType === 'TARGET' ? 'green' : 'red'}
                      variant="light"
                      size="sm"
                    >
                      {location.targetType === 'TARGET' ? 'Targeting' : 'Ausschluss'}
                    </Badge>
                  ),
                },
                {
                  accessor: 'bidModifier',
                  title: 'Gebotsanpassung',
                  width: 130,
                  textAlign: 'right',
                  render: (location) =>
                    location.bidModifier
                      ? `${location.bidModifier > 0 ? '+' : ''}${((location.bidModifier - 1) * 100).toFixed(0)}%`
                      : '-',
                },
                {
                  accessor: 'metrics.impressions',
                  title: 'Impr.',
                  sortable: true,
                  textAlign: 'right',
                  width: 100,
                  render: (location) => formatNumber(location.metrics.impressions),
                },
                {
                  accessor: 'metrics.clicks',
                  title: 'Klicks',
                  sortable: true,
                  textAlign: 'right',
                  width: 80,
                  render: (location) => formatNumber(location.metrics.clicks),
                },
                {
                  accessor: 'metrics.costMicros',
                  title: 'Kosten',
                  sortable: true,
                  textAlign: 'right',
                  width: 100,
                  render: (location) => formatCurrency(location.metrics.costMicros),
                },
                {
                  accessor: 'metrics.conversions',
                  title: 'Conv.',
                  sortable: true,
                  textAlign: 'right',
                  width: 80,
                  render: (location) => location.metrics.conversions.toFixed(1),
                },
              ]}
              noRecordsText="Keine Standorte gefunden"
              minHeight={400}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
